/**
 * Configuration for the Phishing Reporter add-in.
 * Mirrors the VSTO PhisherConfig class structure.
 *
 * Client-specific values are injected at build time via VITE_* environment variables.
 * See clients/{clientId}/config.json and the build script (build.ps1).
 */
export interface PhisherConfig {
  /** Base URL for the API server */
  baseUrl: string;

  /** Report endpoint path template (e.g., "/report/{id}") */
  reportPath: string;

  /** API authentication key */
  apiKey: string;

  /** Header name for the API key (default: "X-Api-Key") */
  apiHeaderName: string;

  /** Email address(es) to send reports to (fallback mode) */
  toAddress: string;

  /** Report action type: "Email", "Ticket", or "Both" */
  reportAction: ReportAction;

  /** Whether to delete the email after reporting */
  deleteAfterReport: boolean;

  /** Plugin version string */
  pluginVersion: string;

  /** Local reports counter */
  reportsCounter: number;
}

export type ReportAction = 'Email' | 'Ticket' | 'Both';

/**
 * Default configuration values.
 * VITE_* env vars override these at build time (set by build.ps1 per client).
 */
export const DEFAULT_CONFIG: PhisherConfig = {
  baseUrl: import.meta.env.VITE_BASE_URL || 'http://10.138.128.14',
  reportPath: import.meta.env.VITE_REPORT_PATH || '/report/{id}',
  apiKey: import.meta.env.VITE_API_KEY || '',
  apiHeaderName: import.meta.env.VITE_API_HEADER_NAME || 'X-RAWAM-Phisher',
  toAddress: import.meta.env.VITE_TO_ADDRESS || 'SOC@alriyadh.gov.sa',
  reportAction: (import.meta.env.VITE_REPORT_ACTION as ReportAction) || 'Both',
  deleteAfterReport: import.meta.env.VITE_DELETE_AFTER_REPORT === 'true',
  pluginVersion: import.meta.env.VITE_PLUGIN_VERSION || 'V1.0-Web',
  reportsCounter: 0,
};

/**
 * Remote configuration response from /plugin endpoint.
 * Backend sends: OutlookPluginType, Email, Method, DeleteAfterReport
 */
export interface RemotePluginConfig {
  reportPath?: string;
  apiKey?: string;
  apiHeaderName?: string;
  // Backend field names (GetPluginConfigurations returns these)
  Email?: string;       // maps to toAddress
  Method?: string;      // maps to reportAction
  DeleteAfterReport?: string; // "True"/"False" string from backend
  // Direct overrides (if ever sent)
  toAddress?: string;
  reportAction?: ReportAction;
  deleteAfterReport?: boolean;
}

/**
 * Check if the configuration is valid for API reporting
 */
export function isConfigValid(config: PhisherConfig): boolean {
  return !!(config.baseUrl && config.baseUrl.trim().length > 0);
}

/**
 * Build the full report URL with campaign ID
 */
export function buildReportUrl(config: PhisherConfig, campaignUserId: string): string {
  const path = config.reportPath.replace('{id}', encodeURIComponent(campaignUserId));
  const baseUrl = config.baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}
